import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { fmtPct, fmtMoney, fmtDate, TotalReturnCell } from './ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live multi-fund table for embedding inside Learn guides.
 *
 * Replaces hand-written yield/fee ranges with the nightly cache, while
 * keeping the guide's editorial columns (underlying, note) alongside.
 */
export default function LiveFundTable({ tickers, meta = {}, hubTo = '/covered-call-etfs' }) {
  const [funds, setFunds] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/covered-call/funds`, { params: { tickers: tickers.join(',') } })
      .then((r) => { if (!cancelled) setFunds(r.data.funds || []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [tickers]);

  if (failed) return null;
  if (!funds) {
    return <div className="not-prose my-6 h-56 rounded-xl bg-slate-100 dark:bg-slate-800/60 animate-pulse" data-testid="live-funds-loading" />;
  }

  const asOf = funds.map((f) => f.as_of).filter(Boolean).sort().slice(-1)[0];

  return (
    <div className="not-prose my-8 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="live-fund-table">
      <div className="px-5 py-3 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 dark:text-cyan-400">
          Live data · updated nightly
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white dark:bg-slate-900/30">
            <tr>
              {['Fund', 'Underlying', 'Yield', 'Paid 12mo', 'Fee', '1Y Price', '1Y Total Return'].map((h, i) => (
                <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap ${i < 2 ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {funds.map((f) => (
              <tr key={f.ticker} className="border-t border-slate-100 dark:border-slate-800/70" data-testid={`live-fund-row-${f.ticker}`}>
                <td className="px-3 py-2.5">
                  <Link to={`/stock/${f.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline">{f.ticker}</Link>
                  {meta[f.ticker]?.note && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 max-w-[300px]">{meta[f.ticker].note}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 text-xs">{meta[f.ticker]?.underlying || '—'}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtPct(f.dividend_yield)}</td>
                <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmtMoney(f.dist_ttm)}</td>
                <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtPct(f.expense_ratio, 2)}</td>
                <td className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${f.nav_change_1y == null ? 'text-slate-400' : f.nav_change_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {fmtPct(f.nav_change_1y, 1, true)}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap"><TotalReturnCell value={f.total_return_1y} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          Total return = price change + distributions received. Prices as of {fmtDate(asOf)}.
        </span>
        <Link to={hubTo} className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 dark:text-cyan-400 hover:underline" data-testid="live-fund-table-hub-link">
          See all 190 option-income ETFs <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
